/**
 * Launcher V2 — the react-query cache reconciliation every write-through
 * playbook save needs (2026-09-02 rip-out, U4).
 *
 * `playbook-draft-store.ts#persistLadder` owns the ladder itself (the
 * `PlaybookDraft` the UI renders from) but deliberately has no `QueryClient`
 * of its own — it is a plain, DOM-free module exercised by `bun:test`
 * (`mini-apps/` has no DOM harness), and `QueryClient` is a React Query
 * concern its callers (`QualifyStep`, `SequenceMappingDrawer`,
 * `SequencesStep`, `ReviewStep`) already hold via `useQueryClient()`. Each
 * call site applies a successful `persistLadder` response through this one
 * helper instead of hand-rolling the same `setQueryData` shape
 * `WaterfallCard`'s reorder success path originated.
 */
import type { QueryClient } from '@tanstack/react-query';

import { libraryQueryKey, matchedMembersPrefixKey, playbookDetailQueryKey } from './query-keys';
import type {
  PlaybooksLibraryResponse,
  PlaybooksPlaybookDetailResponse,
  PlaybooksSavePlaybookRungsResponse,
} from './schemas';

/**
 * Writes a save response's new head directly into the playbook-detail cache
 * (no second `GET` round trip — the response already carries the persisted
 * ladder) and marks the matched-members ladder stale, since a rung
 * add/remove/mapping change can change which members first-match-wins
 * routes to which rung.
 *
 * Also patches this playbook's `rungCount` inside the CACHED
 * `libraryQueryKey()` list (worth-fixing item flagged alongside the
 * Bug 1-4 review, 2026-09-02): the library response's own `playbooks` summary
 * carries a `rungCount` per playbook, rendered wherever that summary list
 * shows up (e.g. the Library table). Without this, an add/remove here left
 * that count stale until the library query happened to refetch on its own —
 * a real but low-severity staleness, not a correctness bug (nothing reads
 * `rungCount` to gate a write), so it's a same-shape `setQueryData` patch
 * rather than a broader invalidation.
 *
 * Play isolation (plan 2026-09-25-002, U5) — the optional `playId` writes
 * the play-scoped playbook-detail entry (`playbookDetailQueryKey(playbookId,
 * playId)`) instead of the template's, and skips the library `rungCount`
 * patch entirely: a play-owned ladder's rung count is not the template's
 * count, and the Library table never shows a play's own rows.
 *
 * F2 fix — a play-mode ladder save can FORK a Library identity (a
 * segmentation `QualifyStep`'s "Add segmentation" picked, or a sequence
 * `SequenceMappingDrawer` mapped/swapped in) into a new play-owned copy: the
 * server answers with the rewritten rungs, but nothing else about this save
 * response says "the Library changed". Without invalidating the play's own
 * `libraryQueryKey(playId)`, that cache keeps serving the PRE-fork rows —
 * the rung then renders the raw forked-copy id as its name (no matching
 * `PlaybooksSequenceSummary`/`PlaybooksSegmentationSummary` in the stale
 * list) and readiness/sender/flow-id popovers that key off the library
 * lookup silently vanish. Invalidated (and awaited, like the matched-members
 * invalidation below) only in play mode — the template's own Library listing
 * never shows a play's forked-off rows, so there is nothing to refresh there.
 */
export async function applyLadderSaveToCache(
  queryClient: QueryClient,
  playbookId: string,
  response: Pick<PlaybooksSavePlaybookRungsResponse, 'versionId' | 'versionNumber' | 'rungs'>,
  playId?: string,
): Promise<void> {
  queryClient.setQueryData(
    playbookDetailQueryKey(playbookId, playId),
    (old: PlaybooksPlaybookDetailResponse | undefined) =>
      old
        ? {
            ...old,
            head: { versionId: response.versionId, versionNumber: response.versionNumber, rungs: response.rungs },
          }
        : old,
  );
  if (playId === undefined) {
    queryClient.setQueryData(
      libraryQueryKey(),
      (old: PlaybooksLibraryResponse | undefined) =>
        old
          ? {
              ...old,
              playbooks: old.playbooks.map((playbook) =>
                playbook.id === playbookId ? { ...playbook, rungCount: response.rungs.length } : playbook,
              ),
            }
          : old,
    );
  } else {
    // F2 fix — see this function's own doc for why a play-mode save can
    // silently fork a Library identity out from under this cache.
    await queryClient.invalidateQueries({ queryKey: libraryQueryKey(playId) });
  }
  await queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() });
}
